import { Unit, UnitType } from '../../Types/unit';
import { AccessTypeKey } from '../../Types/access';
import { transformDateStringPropsToDate } from '../../Utils/dateUtils';
import { get, post } from '../common';

const apiPath = 'api/unit/';

function unitMapper(dto: Unit | undefined | null): Unit | undefined {
	if (!dto) return dto ?? undefined;
	return transformDateStringPropsToDate<Unit, keyof Unit>(dto, ['PublicRegistrationUrlExpiresUtc']);
}

function unitArrayMapper(dto: Unit[] | undefined | null): Unit[] | undefined {
	if (!dto) return dto ?? undefined;
	return dto.map((o) => unitMapper(o)) as Unit[];
}

export async function getRootUnits(accessTypeKey?: AccessTypeKey) {
	const path = `${apiPath}GetRootUnits?accessTypeKey=${accessTypeKey ?? ''}`;
	const rootUnits = await get<Unit[]>(path);
	return unitArrayMapper(rootUnits);
}

export async function getAllUnits(
	accessTypeKey: AccessTypeKey | null,
	includeInactive?: boolean,
	overrideLimitedAccessMode?: boolean
) {
	const path = `${apiPath}GetAllUnits?accessTypeKey=${accessTypeKey}&includeInactive=${includeInactive ?? 'false'}&overrideLimitedAccessMode=${overrideLimitedAccessMode ?? 'false'}`;
	const units = await get<Unit[]>(path);
	return unitArrayMapper(units);
}

export async function getUnitsByParent(parentId: number) {
	const path = `${apiPath}GetUnitsByParent?parentId=${parentId}`;
	const units = await get<Unit[]>(path);
	return unitArrayMapper(units);
}

export async function getUnit(id: number) {
	const path = `${apiPath}GetUnit?unitId=${id}`;
	const unit = await get<Unit>(path);
	return unitMapper(unit);
}

export async function getUnitTypes() {
	const path = `${apiPath}GetAllUnitTypes`;
	const unitTypes = await get<UnitType[]>(path);
	return unitTypes;
}

export async function createUnit(unit: Unit) {
	const path = `${apiPath}CreateUnit`;
	const newUnit = await post<Unit>(path, { unit });
	return unitMapper(newUnit);
}

export async function updateUnit(unit: Unit) {
	const path = `${apiPath}UpdateUnit`;
	const updatedUnit = await post<Unit>(path, { unit });
	return unitMapper(updatedUnit);
}

export async function clearPublicRegistrationLink(unitId: number) {
	const path = `${apiPath}ClearPublicRegistrationLink`;
	await post(path, unitId);
	return true;
}

export async function getPublicRegistrationLink(unitId: number, forceRegenerate: boolean) {
	const path = `${apiPath}GetPublicRegistrationLink?unitId=${unitId}`;

	type PRType = { url: string; expiresUtc: Date };
	const pr = await post<PRType>(path, forceRegenerate);
	if (!pr) return pr;
	return transformDateStringPropsToDate(pr, ['expiresUtc']);
}

export async function getAllowedDomainsForPublicRegistrationLink(unitId: number) {
	const path = `${apiPath}GetAllowedDomainsForUnitPublicRegistrationLink?unitId=${unitId}`;
	const domains = await get<string[]>(path);
	return domains;
}
